import { Component, Inject, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AuthService } from '@app/common/services/auth.service';
import { DocumentModel } from '@app/common/models/document.model';
import { DialogService } from '@app/common/services/dialog.service';
import { Restangular } from 'ngx-restangular';
import { ClassName } from '@app/common/enums/class-name.enum';
import { DocumentEditComponent } from '@app/ps/components/document-edit/document-edit.component';
import { SortModel } from '@app/common/models/sort.model';
import * as _ from 'lodash';
import { UserNamePipe } from '@app/common/pipes/username.pipe';
import { AttachmentTypePipe } from '@app/common/pipes/attachment-type.pipe';
import { ErrorHandlerService } from '@app/common/services/error-handler.service';
import { AttachmentTypeModel } from '@app/common/models/attachment-type.model';
import { UploadService } from '@app/common/services/upload.service';
import { APP_CONFIG } from '@app/common/services/config.service';
import { DocumentsBaseComponent } from '@app/common/components/documents-base/document-base.component';
import { ComponentAddComponent, ComponentAddResult } from '@app/ps/components/component-add/component-add.component';
import { FileUtils } from '@app/common/utils/file.utils';
import { UploadFileExtended } from '@app/common/components/fileupload/fileupload.component';
import { AttachmentChooseTypeOnlyFormComponent } from '@app/common/components/attachment-choose-type-only-form/attachment-choose-type-only-form.component';
import { SimpleTextModalComponent } from '@app/common/components/simple-text-modal/simple-text-modal.component';

@Component({
  selector: 'gmt-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss']
})
export class DocumentsComponent extends DocumentsBaseComponent implements OnInit, OnDestroy, OnChanges {

  public static readonly DEFAULT_FILE_MSG = 'Vložte komponentu (soubor) přetažením, nebo vyberte z počítače.';

  documentSortOrder: SortModel = { sortBy: 'timestamp', direction: 'desc' };
  uploadingFiles: UploadFileExtended[] = [];
  fileMsg = DocumentsComponent.DEFAULT_FILE_MSG;

  constructor(
    authService: AuthService,
    dialogService: DialogService,
    restangular: Restangular,
    usernamePipe: UserNamePipe,
    attachmentTypePipe: AttachmentTypePipe,
    errorHandlerService: ErrorHandlerService,
    uploadService: UploadService,
    @Inject(APP_CONFIG) config,
  ) {
    super(authService, dialogService, restangular, usernamePipe, attachmentTypePipe, errorHandlerService, uploadService, config);

    this.onUpdateDocument = this.onUpdateDocument.bind(this);
    this.onCreateDocument = this.onCreateDocument.bind(this);
    this.onFileSelect = this.onFileSelect.bind(this);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentList']) {
      this.documentSort();
      this.componentSort();
    }
  }

  onCollapseAll(collapse: boolean) {
    this.documentList.list.forEach(d => d.collapsed = collapse);
  }

  async onFileSelect(files: UploadFileExtended[]) {
    let attachmentTypes = [];
    let disabledAttachmentTypes = [];

    if (this.folder && this.folder.opinionRequest) {
      attachmentTypes = AttachmentTypeModel.getByCategory('opinion-mpv');
      disabledAttachmentTypes = [];

      this.documentList.list.forEach(d => {
        d.components.forEach(c => {
          if (!c.cancelled && ['OPINION_SCAN'].includes(c.attachmentType)) {
            disabledAttachmentTypes.push({ attachmentType: c.attachmentType, reason: 'Nelze vložit, již existuje'});
          }
        });
      });
    }

    if (this.folder && this.folder.geometricPlanRequest) {
      attachmentTypes = AttachmentTypeModel.getByCategory('gp-mpv');
      disabledAttachmentTypes = [];
      this.documentList.list.forEach(d => {
        d.components.forEach(c => {
          if (!c.cancelled && ['GEOMETRIC_PLAN_VFK', 'GEOMETRIC_PLAN'].includes(c.attachmentType)) {
            disabledAttachmentTypes.push({ attachmentType: c.attachmentType, reason: 'Nelze vložit, již existuje'});
          }
        });
      });
    }

    if (files.length === 1) {
      const dialog = this.dialogService.open(AttachmentChooseTypeOnlyFormComponent, {
        data: {
          attachmentTypes: attachmentTypes,
          disabledAttachmentTypes: disabledAttachmentTypes
        },
        className: ClassName.HIGHER_DIALOG,
      });

      const sub = dialog.afterClosed.subscribe((result: AttachmentTypeModel) => {
        if (result) {
          const sendData = {
            defaultDocument: true,
            file: files[0].fileEntry,
            filename: files[0].fileEntry.name,
            attachmentType: result.id
          };

          this.uploadService.upload({
            url: this.restBaseUrl + '/documents/create',
            data: {...sendData, ...this.getUploadRelations()},
            headers: { Authorization: this.authService.getToken() }
          }).progress(event => {
            files[0].progress = (100.0 * event.loaded / event.total);
          }).success(data => {
            this.clearFiles(files);
            this.updated.emit();
            this.uploadingFiles.splice(this.uploadingFiles.indexOf(files[0]), 1);
            this.updated.emit(true);
          }).error(() => {
            files[0].error = true;
          });
        } else {
          this.clearFiles(files);
        }

        sub.unsubscribe();
      });
    } else if (files.length > 1) {
      this.fileMsg = 'Je možné vložit pouze jeden soubor.';
    } else {
      this.clearFiles(files);
    }
  }

  onCreateDocument() {
    const dialog = this.dialogService.open(ComponentAddComponent, {
      data: { isCase: (this.folder && this.folder.businessCase) },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: ComponentAddResult) => {
      if (result) {
        const sendData: any = {
          documentName: result.documentName,
          file: result.files[0].fileEntry,
          filename: result.files[0].fileEntry.name,
          attachmentType: result.attachmentType.id,
          note: result.note,
        };

        Object.assign(sendData, this.getUploadRelations());

        return this.uploadService.upload({
          url: this.restBaseUrl + '/documents/create',
          formData: FileUtils.objectToJsonMultipartFormData(sendData),
          headers: { Authorization: this.authService.getToken() }
        }).then(() => {
          this.updated.emit();
        });
      }
      sub.unsubscribe();
    });
  }

  onReturnDocument(document: DocumentModel) {
    const dialog = this.dialogService.open(SimpleTextModalComponent, {
      data: {
        title: 'Opravdu chcete vrátit dokument do EPD?',
        label: 'Poznámka'
      },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: string | boolean) => {
      if (result !== false) {
        const returnNote = result ? { text: result } : null;

        return this.restangular.one('documents', document.id).one('return').customPOST(returnNote).toPromise()
          .then(() => {
            this.updated.emit();
          }, this.errorHandlerService.get);
      }
      sub.unsubscribe();
    });
  }

  onUpdateDocument(document: DocumentModel) {
    const dialog = this.dialogService.open(DocumentEditComponent, {
      data: {
        document: document,
        canEdit: this.canEdit
      },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: DocumentModel | boolean) => {
      if (result) {
        this.restangular.one('documents', document.id).customPOST(result, 'update')
          .toPromise()
          .then(() => {
            this.updated.emit();
          });
      }
      sub.unsubscribe();
    });
  }

  onDocumentSort(sortBy: string) {
    this.documentSortOrder.direction = sortBy === this.documentSortOrder.sortBy
      ? this.documentSortOrder.direction === 'asc' ? 'desc' : 'asc'
      : 'asc';
    this.documentSortOrder.sortBy = sortBy;
    this.documentSort();
  }

  onCreateComponent(document: DocumentModel) {
    const dialog = this.dialogService.open(ComponentAddComponent, {
      data: { document: document, isCase: !!document.businessCase },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: ComponentAddResult) => {
      if (result) {
        const sendData: any = {
          file: result.files[0].fileEntry,
          filename: result.files[0].fileEntry.name,
          attachmentType: result.attachmentType.id,
          note: result.note,
        };

        return this.uploadService.upload({
          url: this.restBaseUrl + `/documents/${document.id}/components/create`,
          formData: FileUtils.objectToJsonMultipartFormData(sendData),
          headers: { Authorization: this.authService.getToken() }
        }).then(() => {
          this.updated.emit();
        });
      }
      sub.unsubscribe();
    });
  }

  onComponentSort(sortBy: string) {
    this.componentSortOrder.direction = sortBy === this.componentSortOrder.sortBy
      ? this.componentSortOrder.direction === 'asc' ? 'desc' : 'asc'
      : 'asc';
    this.componentSortOrder.sortBy = sortBy;
    this.componentSort();
  }

  canClassifyDocument(document: DocumentModel): boolean {
    const documentPlace = !document.folder || (document.folder && this.folder && document.folder.id === this.folder.id);
    const acquisitionDeeds = AttachmentTypeModel.getByCategory('acquisitionDeed').map(a => a.id);
    const caseCheck = !document.folder
      || !document.businessCase
      || !document.businessCase.heliosImportStatus
      || document.components.every(component => !acquisitionDeeds.includes(component.attachmentType));

    return this.canEdit
      && documentPlace
      && document.sourceType === 'EPD'
      && caseCheck;
  }

  canCreateComponent(document: DocumentModel): boolean {
    return this.canEdit
      && document.sourceType === 'MPV'
      && this.uploadDestination
      && (
        // k projektu
        (this.uploadDestination.entityType === undefined
          && !document.businessCase
          && !document.geometricPlanRequest
          && !document.opinionRequest
          && !document.title
          && !document.parcel
          && !document.subject
          && !document.constructionObject
        )
        ||
        // mimo projekt
        (document[this.uploadDestination.entityType]
            && document[this.uploadDestination.entityType].id === this.uploadDestination.id)
      );
  }

  canCreateGeometricPlanRequest() {
    if (this.folder && this.folder.geometricPlanRequest) {
      return ['APPROVED', 'REGISTER_REQUESTED', 'REGISTERED', 'IMPORT_REQUESTED'].includes(this.folder.geometricPlanRequest.status);
    }

    return false;
  }

  canCreateOpinionRequest() {
    if (this.folder && this.folder.opinionRequest) {
      return ['REALIZED'].includes(this.folder.opinionRequest.status);
    }

    return false;
  }

  private documentSort() {
    this.documentList.list = _.orderBy(
      this.documentList.list,
      [((document: DocumentModel) => document.folder && this.folder && document.folder.id === this.folder.id ? 0 : 1), this.documentSortOrder.sortBy],
      ['asc', this.documentSortOrder.direction as 'asc' | 'desc']
    );
  }

  private componentSort() {
    this.documentList.list.forEach((item) => {
      item.components = _.orderBy(
        item.components,
        this.getComponentSortFunction(this.componentSortOrder.sortBy),
        this.componentSortOrder.direction as any);
    });
  }

  private clearFiles(files: UploadFileExtended[]) {
    this.uploadingFiles.splice(0, this.uploadingFiles.length);
    files.splice(0, files.length);
    this.fileMsg = DocumentsComponent.DEFAULT_FILE_MSG;
  }
}
